import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { GridRowClickEvent } from "@progress/kendo-react-grid";
import { MenuItem } from "@progress/kendo-react-layout";
import { DateRangePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { FilterDescriptor, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { Form } from "react-bootstrap";
import { getUserInfo } from "../../app";
import {
  AttendanceStatus,
  CalendarEventModel,
  LocationProvidersModel,
  UpdateCalendarEvent,
} from "../../data/entities";
import {
  Breakpoints,
  GridSearchBox,
  ODataGrid,
  ResponsiveGridColumn,
  TitlelessCard,
} from "../../components";
import { useGridSearch, useSetPageTitle, useSharedState } from "../../hooks";
import { CalendarEventService, LocationService } from "../../services";
import {
  appendODataFilters,
  createODataSearchFilters,
  formatTimeOnly,
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
  toYearMonthDayLocalString,
} from "../../utils";
import { CalendarBookingModal } from "../calendar/components";
import { getEventEnd, getEventStart } from "../home/hooks";
import {
  AppointmentDateCell,
  AppointmentTimeCell,
  AttendanceStatusCell,
  ClientNameCell,
} from "./components";

interface AppointmentFilters {
  dateFrom: Date | null;
  dateTo: Date | null;
  locationId: string;
  status: string;
  includeCancelled: boolean;
}

const EMPTY_FILTERS: AppointmentFilters = {
  dateFrom: null,
  dateTo: null,
  locationId: "-1",
  status: "",
  includeCancelled: false,
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: AttendanceStatus.Pending as string, label: "Booked" },
  { value: AttendanceStatus.CheckedIn as string, label: "Checked in" },
  { value: AttendanceStatus.NoShow as string, label: "No-show" },
];

const SEARCH_FIELDS = [
  "customerFirstName",
  "customerLastName",
  "phoneNumber",
  "serviceName",
  "providerName",
  "locationName",
];

const FilterLabel = ({ children }: { children: string }) => (
  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 11, letterSpacing: "0.5px" }}>
    {children}
  </div>
);

export function AppointmentsIndexPage() {
  const currentUserId = useSelector(getUserInfo())?.id;

  const calendarEventService = useMemo(() => new CalendarEventService(), []);
  const locationService = useMemo(() => new LocationService(), []);

  const gridRef = useRef<ODataGrid>(null);
  const gridData = useSharedState<Array<CalendarEventModel>>([]);

  const [accessibleLocations, setAccessibleLocations] = useState<LocationProvidersModel[]>([]);
  const [editingEvent, setEditingEvent] = useState<CalendarEventModel | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [filters, setFilters] = useState<AppointmentFilters>(EMPTY_FILTERS);
  const { searchText, setSearchText } = useGridSearch(gridRef);

  useSetPageTitle("Appointments", []);

  useEffect(() => {
    const initialize = async () => {
      if (!currentUserId) return;
      try {
        const locations = await locationService.getLocationsByRoleAccess(currentUserId);
        const sortedLocations = (locations ?? [])
          .map((location) => ({
            ...location,
            details: location.details?.filter((d) => d.isActive) ?? [],
          }))
          .sort((a, b) => a.locationName.localeCompare(b.locationName));
        setAccessibleLocations(sortedLocations);
      } catch {
        showErrorMessage("Error loading locations");
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // The loader only refetches on Kendo dataState changes, so force a reload when
  // the sidebar filters change (useGridSearch does the same for the search box).
  // Skip the initial mount (the grid loads itself).
  const isFirstFilterRun = useRef(true);
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    gridRef.current?.refreshData();
  }, [filters]);

  const dropdownResults = useMemo(
    () => accessibleLocations.flatMap((location) => location.details),
    [accessibleLocations]
  );

  const locationOptions = useMemo(
    () => [
      { value: "-1", text: "All locations" },
      ...accessibleLocations.map((location) => ({
        value: location.locationId.toString(),
        text: location.locationName,
      })),
    ],
    [accessibleLocations]
  );

  const [locationFilter, setLocationFilter] = useState<FilterDescriptor | CompositeFilterDescriptor | null>(null);
  const filteredLocationOptions = useMemo(
    () => (locationFilter ? filterBy(locationOptions.slice(), locationFilter) : locationOptions),
    [locationOptions, locationFilter]
  );


  const buildQuery = (oDataQuery: string) => {
    const extraFilters: string[] = [];

    if (filters.dateFrom) extraFilters.push(`date ge ${toYearMonthDayLocalString(filters.dateFrom)}`);
    if (filters.dateTo) extraFilters.push(`date le ${toYearMonthDayLocalString(filters.dateTo)}`);
    if (filters.locationId !== "-1") extraFilters.push(`locationId eq ${filters.locationId}`);
    if (filters.status === AttendanceStatus.Pending) {
      extraFilters.push(`(attendanceStatus eq '${AttendanceStatus.Pending}' or attendanceStatus eq null)`);
    } else if (filters.status) {
      extraFilters.push(`attendanceStatus eq '${filters.status}'`);
    }
    if (!filters.includeCancelled) extraFilters.push("isCancelled ne true");

    extraFilters.push(...createODataSearchFilters(searchText, SEARCH_FIELDS));

    return appendODataFilters(oDataQuery, extraFilters);
  };

  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const setAttendanceStatus = async (row: CalendarEventModel, attendanceStatus: AttendanceStatus) => {
    try {
      await calendarEventService.updateAttendanceStatusAsync(row.bookingEventId, attendanceStatus);
      const statusLabel = attendanceStatus === AttendanceStatus.NoShow
        ? "Marked as no-show."
        : "Attendance status cleared.";
      showSuccessMessage(statusLabel);
      gridRef.current?.refreshData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        "Failed to update attendance status.";
      showErrorMessage(errorMessage);
    }
  };

  const handleRowMenuSelect = (event: any, dataItem: any) => {
    const action = event.item?.data?.action;
    const row = dataItem as CalendarEventModel;
    if (!row) return;

    switch (action) {
      case "open":
        setEditingEvent(row);
        break;
      case "noshow":
        if (row.attendanceStatus === AttendanceStatus.NoShow) return;
        if (getEventStart(row) > new Date()) {
          showWarningMessage("Available once the appointment start time has passed");
          return;
        }
        setAttendanceStatus(row, AttendanceStatus.NoShow);
        break;
      case "undoNoshow":
        if (row.attendanceStatus !== AttendanceStatus.NoShow) return;
        setAttendanceStatus(row, AttendanceStatus.Pending);
        break;
    }
  };

  const handleRowClick = (e: GridRowClickEvent) => setEditingEvent(e.dataItem);

  const editingDataItem = editingEvent
    ? {
        id: editingEvent.bookingEventId,
        bookingEventId: editingEvent.bookingEventId,
        customerFirstName: editingEvent.customerFirstName,
        customerLastName: editingEvent.customerLastName,
        email: editingEvent.email,
        phoneNumber: editingEvent.phoneNumber,
        locationId: editingEvent.locationId,
        providerId: editingEvent.providerId,
        providerName: editingEvent.providerName,
        serviceId: editingEvent.serviceId,
        start: getEventStart(editingEvent),
        end: getEventEnd(editingEvent),
        notes: editingEvent.notes,
        attendanceStatus: editingEvent.attendanceStatus,
      }
    : null;

  const handleAppointmentSubmit = async (formEvent: any) => {
    if (!editingEvent) return;
    const values = formEvent.value;
    try {
      const update: UpdateCalendarEvent = {
        ...editingEvent,
        customerFirstName: values.customerFirstName,
        customerLastName: values.customerLastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        serviceId: values.serviceId,
        locationId: values.locationId,
        providerId: values.providerId,
        startDate: toYearMonthDayLocalString(values.start),
        startTime: formatTimeOnly(values.start) ?? "",
        endDate: toYearMonthDayLocalString(values.end),
        endTime: formatTimeOnly(values.end) ?? "",
        notes: values.notes,
        attendanceStatus: values.attendanceStatus,
      };
      const result = await calendarEventService.updateCalendarEventAsync(update);
      if (result.warning) {
        showWarningMessage(`Updated with warning: ${result.warning}`);
      } else {
        showSuccessMessage("Updated successfully.");
      }
      setEditingEvent(null);
      gridRef.current?.refreshData();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An unexpected error occurred.";
      showErrorMessage(errorMessage);
    }
  };

  const handleAppointmentDelete = async (dataItem: any) => {
    try {
      const result = await calendarEventService.deleteCalendarEventAsync(dataItem.bookingEventId);
      if (result) {
        showSuccessMessage("Event deleted successfully.");
      }
      gridRef.current?.refreshData();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An unexpected error occurred.";
      showErrorMessage(errorMessage);
    }
  };

  return (
    <div className="row g-3">
      <div className="col-24 col-lg-7 col-xl-6 col-xxl-5">
        <TitlelessCard className="border" bodyClassName="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">Filters</h5>
            <button
              type="button"
              className="btn btn-link btn-sm text-decoration-none p-0"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>

          <FilterLabel>Date range</FilterLabel>
          <DateRangePicker
            style={{ width: "100%" }}
            value={{ start: filters.dateFrom, end: filters.dateTo }}
            format="MMM d, yyyy"
            startDateInputSettings={{ label: "", placeholder: "Start", width: "100%" }}
            endDateInputSettings={{ label: "", placeholder: "End", width: "100%" }}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.value.start, dateTo: e.value.end })}
          />

          <div className="mt-3">
            <FilterLabel>Location</FilterLabel>
            <DropDownList
              className="w-100"
              data={filteredLocationOptions}
              textField="text"
              dataItemKey="value"
              filterable={true}
              onFilterChange={(e) => setLocationFilter(e.filter)}
              onClose={() => setLocationFilter(null)}
              value={locationOptions.find((option) => option.value === filters.locationId) ?? locationOptions[0]}
              onChange={(e) => setFilters({ ...filters, locationId: e.value?.value ?? "-1" })}
            />
          </div>

          <div className="mt-3">
            <FilterLabel>Status</FilterLabel>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <Form.Check
                key={option.value || "all"}
                type="radio"
                name="appointmentsStatusFilter"
                id={`appointments-status-${option.value || "all"}`}
                label={option.label}
                checked={filters.status === option.value}
                onChange={() => setFilters({ ...filters, status: option.value })}
              />
            ))}
          </div>

          <hr className="my-3" />

          <Form.Check
            type="checkbox"
            id="appointments-include-cancelled"
            label="Include cancelled"
            checked={filters.includeCancelled}
            onChange={(e) => setFilters({ ...filters, includeCancelled: e.target.checked })}
          />
        </TitlelessCard>
      </div>

      <div className="col-24 col-lg-17 col-xl-18 col-xxl-19">
        <div className="d-flex flex-wrap align-items-center mb-2">
          <span className="text-muted small">
            Showing {totalCount !== null ? <strong>{totalCount}</strong> : "…"} appointment{totalCount === 1 ? "" : "s"}
          </span>
          <div className="ms-auto">
            <GridSearchBox onSearch={setSearchText} />
          </div>
        </div>

        <ODataGrid
            ref={gridRef}
            getData={async (oDataQuery) => {
              const response = await calendarEventService.query(buildQuery(oDataQuery));
              setTotalCount(response["@odata.count"] ?? null);
              return response;
            }}
            dataState={gridData}
            scrollable="scrollable"
            pageSize={10}
            filterable={false}
            sort={[{ field: "date", dir: "desc" }, { field: "startTime", dir: "desc" }]}
            onRowClick={handleRowClick}
            onRowMenuSelect={handleRowMenuSelect}
          >
            <ResponsiveGridColumn
              title="Date"
              field="date"
              sortable={true}
              cell={AppointmentDateCell}
            />
            <ResponsiveGridColumn
              title="Time"
              field="startTime"
              sortable={true}
              cell={AppointmentTimeCell}
            />
            <ResponsiveGridColumn
              title="Client"
              field="customerLastName"
              sortable={true}
              cell={ClientNameCell}
            />
            <ResponsiveGridColumn
              title="Phone"
              field="phoneNumber"
              sortable={true}
              breakpoint={Breakpoints.xl}
            />
            <ResponsiveGridColumn
              title="Service"
              field="serviceName"
              sortable={true}
              breakpoint={Breakpoints.lg}
            />
            <ResponsiveGridColumn
              title="Provider"
              field="providerName"
              sortable={true}
              breakpoint={Breakpoints.md}
            />
            <ResponsiveGridColumn
              title="Location"
              field="locationName"
              sortable={true}
              breakpoint={Breakpoints.lg}
            />
            <ResponsiveGridColumn
              title="Status"
              field="attendanceStatus"
              sortable={true}
              cell={AttendanceStatusCell}
            />

            <MenuItem text="Open" data={{ action: "open" }} icon="hyperlink-open" />
            <MenuItem text="Mark as no-show" data={{ action: "noshow" }} icon="cancel" />
            <MenuItem text="Undo no-show" data={{ action: "undoNoshow" }} icon="undo" />
        </ODataGrid>
      </div>

      {editingEvent && editingDataItem && (
        <CalendarBookingModal
          key={editingEvent.bookingEventId}
          {...({} as any)}
          dataItem={editingDataItem}
          dropdownResults={dropdownResults}
          onSubmit={handleAppointmentSubmit as any}
          onCancel={() => setEditingEvent(null)}
          onDeleteAppointment={handleAppointmentDelete}
        />
      )}
    </div>
  );
}

export default AppointmentsIndexPage;
